// eslint-disable-next-line
import React, { useEffect } from 'react';
import { IconButton, Paper, List, ListItem, ListItemText, Card, Typography, CardContent } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronRight } from '@mui/icons-material';
import { AppRoute } from '../../routes';
import { GenericToolbar } from '../../components/GenericToolbar';
import { ContentContainer } from '../../components/ContentContainer';
import { pushAppRoute } from '../../data/domain/route-manager';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import _ from 'lodash';
import { DataIndicators } from '../../components/DataIndicators';
import { isDataLoaded } from '../../types/data-providers';
import { useLegalContentData, LegalContentDataProvider } from '../../data/DataProviders/LegalContentDataProvider';
import { Actions } from '../../actions';
import { Selectors } from '../../selectors';
import { useCurrentAppRoute } from '../../hooks/app-route-hooks';
import { PDFRender } from '../../components/PDFRender/PDFRender';
import {
    EULA_FILE_PATH,
    EATON_AT_PLUS_EULA_FILE_PATH,
    EATON_WIRELESS_CONTROLLER_EULA_FILE_PATH,
    EATON_WIRELESS_CONTROLLER_EULA_EATON_FILE_PATH,
    EATON_WIRELESS_CONTROLLER_OSS_FILE_PATH,
    EATON_WIRELESS_CONTROLLER_OSS_EATON_FILE_PATH,
    OPEN_SOURCE_BUILDING_SAFETY_MANAGER_FILE_PATH,
    OPEN_SOURCE_BUILDING_SAFETY_MANAGER_CLOUD_FILE_PATH,
} from '../../constants';
import MediaQuery from 'react-responsive';

const OPEN_SOURCE_PDF_CONTENT: Record<string, { filePath: string; pageCount: number }> = {
    'eula-wireless-controller': {
        filePath: EATON_WIRELESS_CONTROLLER_EULA_FILE_PATH,
        pageCount: 12,
    },
      'eula - eaton at+ controller': {
        filePath: EATON_AT_PLUS_EULA_FILE_PATH,
        pageCount: 9,
    },
    'oss-wireless-controller': {
        filePath: EATON_WIRELESS_CONTROLLER_OSS_FILE_PATH,
        pageCount: 108,
    },
    'eula- wireless': {
        filePath: EATON_WIRELESS_CONTROLLER_EULA_FILE_PATH,
        pageCount: 12,
    },
    oss: {
        filePath: EATON_WIRELESS_CONTROLLER_OSS_FILE_PATH,
        pageCount: 108,
    },
    'eula - eaton wireless controller': {
        filePath: EATON_WIRELESS_CONTROLLER_EULA_EATON_FILE_PATH,
        pageCount: 12,
    },
    'oss - eaton wireless controller': {
        filePath: EATON_WIRELESS_CONTROLLER_OSS_EATON_FILE_PATH,
        pageCount: 215,
    },
    'open source licensing - eaton wireless controller': {
        filePath: EATON_WIRELESS_CONTROLLER_OSS_EATON_FILE_PATH,
        pageCount: 215,
    },
    'oss - open source licensing - eaton wireless controller': {
        filePath: EATON_WIRELESS_CONTROLLER_OSS_EATON_FILE_PATH,
        pageCount: 215,
    },
    'legal.open_source_bsm': {
        filePath: OPEN_SOURCE_BUILDING_SAFETY_MANAGER_FILE_PATH,
        pageCount: 426,
    },
    'open source licensing - building safety manager': {
        filePath: OPEN_SOURCE_BUILDING_SAFETY_MANAGER_FILE_PATH,
        pageCount: 426,
    },
    'open source licensing - building safety manager (cloud)': {
        filePath: OPEN_SOURCE_BUILDING_SAFETY_MANAGER_CLOUD_FILE_PATH,
        pageCount: 426,
    },
    'legal.open_source_bsm_cloud': {
        filePath: OPEN_SOURCE_BUILDING_SAFETY_MANAGER_CLOUD_FILE_PATH,
        pageCount: 420,
    },
    'licence.eula_wireless_controller': {
        filePath: EATON_WIRELESS_CONTROLLER_EULA_EATON_FILE_PATH,
        pageCount: 12,
    },
    'licence.oss_wireless_controller': {
        filePath: EATON_WIRELESS_CONTROLLER_OSS_EATON_FILE_PATH,
        pageCount: 215,
    },
};

const OPEN_SOURCE_TEXT_CONTENT: Record<string, string> = {
};

const normalizeOpenSourceName = (name: string): string => {
    try {
        return decodeURIComponent(name).trim().toLowerCase();
    } catch (err) {
        return name.trim().toLowerCase();
    }
};

const decodeOpenSourceName = (name: string): string => {
    try {
        return decodeURIComponent(name);
    } catch (err) {
        return name;
    }
};

export const LegalContent: React.FC<React.PropsWithChildren<React.PropsWithChildren<{
    title: string;
    backRoute: AppRoute;
    contentTitle?: string;
}>>> = (props) => {
    const history = useHistory();
    const legalContentData = useLegalContentData();

    const isEmpty = isDataLoaded(legalContentData) && _.isEmpty(legalContentData.value.content);
    return (
        <>
            <GenericToolbar
                title={props.title}
                showBackButton
                backButtonOnClick={(): void => pushAppRoute(history, props.backRoute)}
            />
            <ContentContainer data-testid={'container'}>
                <DataIndicators
                    errorMessage={`Error Loading ${props.contentTitle}`}
                    emptyMessage={`No ${props.contentTitle} found`}
                    data={legalContentData}
                    emptyIcon="Devices"
                    isEmpty={isEmpty}
                />
                {isDataLoaded(legalContentData) && !isEmpty && (
                    <LegalContentDetails content={legalContentData.value.content || ''} title={props.contentTitle} />
                )}
            </ContentContainer>
        </>
    );
};

export const OpenSourceList: React.FC<React.PropsWithChildren<React.PropsWithChildren<{ backRoute: AppRoute }>>> = (props) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const { t } = useLanguageLocale();
    const legal = useSelector(Selectors.Legal.legal);

    useEffect(() => {
        dispatch(Actions.Legal.loadOpenSourceSucceeded());
    }, [dispatch]);

    const items = legal && legal.openSourceList ? legal.openSourceList : [];

    return (
        <>
            <GenericToolbar
                title={t('LEGAL.OPENSOURCELICENSES')}
                showBackButton
                backButtonOnClick={(): void => pushAppRoute(history, props.backRoute)}
            />
            <ContentContainer>
                <Paper style={{ textAlign: 'justify' }}>
                    <List className="list" disablePadding data-testid={'open-source-menu'}>
                        {items.map((
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            value: any
                        ) => {
                            const isTranslationKey =
                                typeof value.name === 'string' &&
                                (value.name.startsWith('LEGAL.') || value.name.startsWith('LICENCE.'));
                            const displayName = isTranslationKey ? t(value.name) : value.name;
                            return (
                                <ListItem
                                    className="selected"
                                    divider={true}
                                    style={{ height: 72, cursor: 'pointer' }}
                                    key={value.id}
                                    onClick={(): void => {
                                        pushAppRoute(
                                            history,
                                            { type: 'OpenSourceContent', name: value.name },
                                            { displayTitle: displayName }
                                        );
                                    }}
                                >
                                    <ListItemText
                                        primary={displayName}
                                        primaryTypographyProps={{
                                            variant: 'body1',
                                            style: { fontWeight: 600 },
                                        }}
                                    ></ListItemText>
                                    <IconButton>
                                        <ChevronRight />
                                    </IconButton>
                                </ListItem>
                            );
                        })}
                    </List>
                </Paper>
            </ContentContainer>
        </>
    );
};

export const OpenSourceContent: React.FC<React.PropsWithChildren<React.PropsWithChildren<{ backRoute: AppRoute }>>> = (props) => {
    const currentAppRoute = useCurrentAppRoute();
    const location = useLocation<{
        backRouteType?: AppRoute['type'];
        displayTitle?: string;
        legalDoc?:
            | 'EULA_WIRELESS'
            | 'EULA_WIRELESS_EATON'
            | 'EULA_AT_PLUS'
            | 'OSS_WIRELESS'
            | 'OSS_WIRELESS_EATON';
    }>();
    const resolvedBackRoute: AppRoute =
        location.state?.backRouteType === 'Legal' ? { type: 'Legal' } : props.backRoute;

    if ('name' in currentAppRoute) {
        const pdfByDocType =
            location.state?.legalDoc === 'EULA_WIRELESS'
                ? {
                    filePath: EATON_WIRELESS_CONTROLLER_EULA_FILE_PATH,
                    pageCount: 12,
                }
                : location.state?.legalDoc === 'EULA_WIRELESS_EATON'
                    ? {
                        filePath: EATON_WIRELESS_CONTROLLER_EULA_EATON_FILE_PATH,
                        pageCount: 12,
                    }
                : location.state?.legalDoc === 'EULA_AT_PLUS'
                    ? {
                        filePath: EATON_AT_PLUS_EULA_FILE_PATH,
                        pageCount: 9,
                    }
                : location.state?.legalDoc === 'OSS_WIRELESS'
                    ? {
                        filePath: EATON_WIRELESS_CONTROLLER_OSS_FILE_PATH,
                        pageCount: 108,
                    }
                        : location.state?.legalDoc === 'OSS_WIRELESS_EATON'
                            ? {
                                filePath: EATON_WIRELESS_CONTROLLER_OSS_EATON_FILE_PATH,
                                pageCount: 108,
                            }
                    : undefined;
        const normalizedName = normalizeOpenSourceName(currentAppRoute.name);
        const pdfContent = pdfByDocType || OPEN_SOURCE_PDF_CONTENT[normalizedName];
        if (pdfContent) {
            return (
                <OpenSourcePdfContent
                    title={location.state?.displayTitle || decodeOpenSourceName(currentAppRoute.name)}
                    filePath={pdfContent.filePath}
                    pageCount={pdfContent.pageCount}
                    backRoute={resolvedBackRoute}
                />
            );
        }

        const textFileName = OPEN_SOURCE_TEXT_CONTENT[normalizedName];
        if (textFileName) {
            return (
                <LegalContentDataProvider filename={textFileName}>
                    <LegalContent title={currentAppRoute.name} backRoute={resolvedBackRoute} />
                </LegalContentDataProvider>
            );
        }

        return (
            <LegalContentDataProvider filename={`${currentAppRoute.name}.txt`}>
                <LegalContent title={currentAppRoute.name} backRoute={resolvedBackRoute} />
            </LegalContentDataProvider>
        );
    }

    return null;
};

const OpenSourcePdfContent: React.FC<
    React.PropsWithChildren<
        React.PropsWithChildren<{
            title: string;
            filePath: string;
            pageCount: number;
            backRoute: AppRoute;
        }>
    >
> = (props) => {
    const history = useHistory();

    return (
        <>
            <GenericToolbar
                title={props.title}
                showBackButton
                backButtonOnClick={(): void => pushAppRoute(history, props.backRoute)}
            />
            <ContentContainer data-testid={'container'}>
                {process.env.NODE_ENV !== 'test' && (
                    <>
                        <MediaQuery maxWidth={450}>
                            <PDFRender file={props.filePath} pageCount={props.pageCount} width={380} />
                        </MediaQuery>
                        <MediaQuery minWidth={450} maxWidth={800}>
                            <PDFRender file={props.filePath} pageCount={props.pageCount} width={600} />
                        </MediaQuery>
                        <MediaQuery minWidth={801}>
                            <PDFRender file={props.filePath} pageCount={props.pageCount} width={900} />
                        </MediaQuery>
                    </>
                )}
            </ContentContainer>
        </>
    );
};

const LegalContentDetails: React.FC<React.PropsWithChildren<React.PropsWithChildren<{ content: string; title?: string }>>> = (props) => {
    const { t } = useLanguageLocale();
    return (
        <>
            <MediaQuery  maxWidth={450}>
                {props.title === t('LEGAL.EULA') && process.env.NODE_ENV !== 'test' && (
                    <PDFRender file={EULA_FILE_PATH} pageCount={18} width={380} />
                )}
            </MediaQuery>
            <MediaQuery minWidth={450} maxWidth={800}>
                {props.title === t('LEGAL.EULA') && process.env.NODE_ENV !== 'test' && (
                    <PDFRender file={EULA_FILE_PATH} pageCount={18} width={600} />
                )}
            </MediaQuery>
            <MediaQuery minWidth={801} >
                {props.title === t('LEGAL.EULA') && process.env.NODE_ENV !== 'test' && (
                    <PDFRender file={EULA_FILE_PATH} pageCount={18} width={900} />
                )}
            </MediaQuery>
            {(props.title !== t('LEGAL.EULA') || process.env.NODE_ENV === 'test') && (
                <Paper style={{ textAlign: 'justify', maxWidth: 1200, marginLeft: '5%' , marginRight: '5%' }} data-testid={'eula-content'}>
                    <Card>
                        {props.title && (
                            <Typography variant={'h4'} style={{ fontWeight: 600, textAlign: 'center', padding: 28 }}>
                                {props.title}
                            </Typography>
                        )}
                        <CardContent>
                            <Typography component="pre" style={{ whiteSpace: 'pre-wrap' }}>
                                {props.content}
                            </Typography>
                        </CardContent>
                    </Card>
                </Paper>
            )}
        </>
    );
};
